import { Box } from '@mui/material';
import { SupplyChainEditIcon } from 'assets/img/icons';
import ComponentItemTable from 'components/ComponentsLibrary/ComponentItemsTable';
import { ComponentItemTableColumnKeys } from 'components/ComponentsLibrary/constants/headerColumns';
import { Loader } from 'components/Forms';
import { ErrorState, PageContainer, PageSubTitle, PageTitle } from 'components/Structure';
import FlexBox from 'components/Structure/FlexBox';
import OverviewHeader from 'components/Structure/OverviewHeader/OverviewHeader';
import { CardIllustration, EmptyStateCard, ThemeButton, ThemeTypography } from 'designSystem';
import Image from 'designSystem/DataDisplay/Image/Image';
import Icon from 'designSystem/Primitives/Icon/Icon';
import useComponentChain from 'hooks/useComponentChain';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { IComponentItem } from 'types/component.types';
import { AvailableSizes, BackgroundTypes, IllustrationTypes, ItemTypes } from 'types/enums';

const ComponentChainOverview: FC = () => {
  const { componentChain, loading, error, handleEditComponentChain } = useComponentChain();
  const navigate = useNavigate();

  if (loading) {
    return <Loader />;
  }
  if (error || !componentChain) {
    return <ErrorState />;
  }

  const handleMappingClick = () => {
    navigate(`editor`);
  };

  const handleComponentRowClick = (component: IComponentItem) => {
    navigate(`/components/${component.id}`);
  };

  return (
    <PageContainer>
      <PageTitle
        title="Component chain overview"
        titleExtension={componentChain.title}
        goBackLabel="All component chains"
        goBackUrl="/component-chains"
      />
      <Box mt={4}>
        <OverviewHeader image={componentChain.image} placeholderIconName="chain-placeholder">
          <FlexBox alignContent="center" mb={1}>
            <ThemeTypography variant="TITLE_MEDIUM">{componentChain.title}</ThemeTypography>
          </FlexBox>

          <FlexBox mb={2} gap={1}>
            {!!componentChain.outputComponents?.length && (
              <FlexBox gap={0.5}>
                <Icon name="raw-material" color="gray-80" />
                <ThemeTypography
                  variant="BODY_MEDIUM_BOLD"
                  color="GRAY_80"
                  maxWidth={300}
                  autoOverflow
                >
                  {componentChain.outputComponents
                    .flatMap(({ rawMaterials }) => rawMaterials)
                    .reduce(
                      (prev, rawMaterial) =>
                        prev ? `${prev}, ${rawMaterial.title}` : rawMaterial.title,
                      ''
                    )}
                </ThemeTypography>
              </FlexBox>
            )}

            {componentChain.ownedBy && (
              <FlexBox gap={0.5}>
                <Image
                  image={componentChain.ownedBy?.logo}
                  backupIcon="partner"
                  width={12}
                  height={12}
                />
                <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                  {componentChain.ownedBy.name}
                </ThemeTypography>
              </FlexBox>
            )}
          </FlexBox>

          <ThemeButton
            startIcon={<Icon name="edit" />}
            size="large"
            color="BLUE_ICE"
            loading={false}
            onClick={() => handleEditComponentChain(componentChain)}
          >
            Edit component chain
          </ThemeButton>
        </OverviewHeader>
      </Box>

      {!!componentChain.outputComponents?.length && (
        <Box flex={1} mt={4}>
          <PageSubTitle title="Component that outputs from this chain" />
          <Box>
            <ComponentItemTable
              hideHeader
              hideDelete
              components={componentChain.outputComponents}
              columns={[
                ComponentItemTableColumnKeys.TITLE,
                ComponentItemTableColumnKeys.PARTNER,
                ComponentItemTableColumnKeys.MODIFIED_TIMESTAMP,
                ComponentItemTableColumnKeys.ACTIONS,
              ]}
              onSelect={handleComponentRowClick}
            />
          </Box>
        </Box>
      )}

      <Box mt={4}>
        <EmptyStateCard
          title="Map this component chain and re-use it in your different product’s supply chains"
          variant={ItemTypes.CHAIN_MAPPING}
          buttonIcon={<SupplyChainEditIcon />}
          buttonText="Map component chain"
          background={BackgroundTypes.WORLD}
          size={AvailableSizes.MEDIUM}
          renderIllustration={
            <CardIllustration image={IllustrationTypes.LOCATIONS} width={300} right={25} top={0} />
          }
          onCreateItemClick={handleMappingClick}
        />
      </Box>
    </PageContainer>
  );
};

export default ComponentChainOverview;
